import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Pagination,
  Collapse,
  IconButton
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useState } from 'react'
import { DCPriceComparison, DistributorOffer } from '../../types/dc.types'
import LandingCostBadge from './LandingCostBadge'

interface PriceComparisonGridProps {
  comparisons: DCPriceComparison[]
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}

interface ExpandableRowProps {
  comparison: DCPriceComparison
}

function ExpandableRow({ comparison }: ExpandableRowProps) {
  const [open, setOpen] = useState(false)
  const offers: DistributorOffer[] = comparison.all_offers || []

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { backgroundColor: 'action.hover' },
          cursor: offers.length > 1 ? 'pointer' : 'default'
        }}
        onClick={() => offers.length > 1 && setOpen(!open)}
      >
        <TableCell>
          {offers.length > 1 && (
            <IconButton size="small">
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {comparison.medicine_name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {comparison.pack_size}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={comparison.best_distributor_name || 'Unknown'}
            color="primary"
          />
        </TableCell>
        <TableCell align="right">
          {comparison.best_ptr ? `Rs.${comparison.best_ptr.toFixed(2)}` : '-'}
        </TableCell>
        <TableCell align="right">
          {comparison.best_discount_pct ? (
            <Chip
              size="small"
              label={`${comparison.best_discount_pct}%`}
              color="secondary"
            />
          ) : '-'}
        </TableCell>
        <TableCell align="center">
          {comparison.best_offer_text || '-'}
        </TableCell>
        <TableCell align="right">
          <LandingCostBadge
            landingCost={comparison.best_offer_landing_cost ?? comparison.best_landing_cost}
            effectiveDiscount={comparison.best_effective_discount}
            showEffective
          />
        </TableCell>
        <TableCell align="center">
          <Chip
            size="small"
            label={comparison.offer_count}
            variant="outlined"
          />
        </TableCell>
      </TableRow>

      {/* Expanded Row - All Offers */}
      {offers.length > 1 && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  All Offers
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Distributor</TableCell>
                      <TableCell align="right">PTR</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="center">Offer</TableCell>
                      <TableCell align="right">Landing Cost</TableCell>
                      <TableCell align="right">Diff from Best</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offers.map((offer, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: idx === 0 ? 'success.50' : 'inherit'
                        }}
                      >
                        <TableCell>
                          {idx === 0 ? (
                            <Chip size="small" label="Best" color="success" />
                          ) : (
                            `#${idx + 1}`
                          )}
                        </TableCell>
                        <TableCell>{offer.distributor_name}</TableCell>
                        <TableCell align="right">
                          Rs.{offer.ptr?.toFixed(2) || '-'}
                        </TableCell>
                        <TableCell align="right">
                          {offer.discount_pct ? `${offer.discount_pct}%` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {offer.offer_text || '-'}
                        </TableCell>
                        <TableCell align="right">
                          <LandingCostBadge
                            landingCost={offer.offer_landing_cost ?? offer.landing_cost}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {idx === 0 ? (
                            '-'
                          ) : (() => {
                            const diff = (offer.offer_landing_cost ?? offer.landing_cost ?? 0) -
                              (offers[0].offer_landing_cost ?? offers[0].landing_cost ?? 0)
                            return (
                              <Typography color={diff > 0 ? 'error.main' : 'success.main'}>
                                {diff >= 0 ? '+' : '-'}Rs.{Math.abs(diff).toFixed(2)}
                              </Typography>
                            )
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export default function PriceComparisonGrid({
  comparisons,
  page,
  totalPages,
  onPageChange
}: PriceComparisonGridProps) {
  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="5%" />
              <TableCell width="25%">Medicine</TableCell>
              <TableCell width="15%">Best Distributor</TableCell>
              <TableCell width="10%" align="right">PTR</TableCell>
              <TableCell width="10%" align="right">Discount</TableCell>
              <TableCell width="10%" align="center">Offer</TableCell>
              <TableCell width="15%" align="right">Landing Cost</TableCell>
              <TableCell width="10%" align="center">Offers</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comparisons.map((comparison) => (
              <ExpandableRow key={comparison.id} comparison={comparison} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" p={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => onPageChange(value)}
            color="primary"
          />
        </Box>
      )}
    </Paper>
  )
}
